import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Divider,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
} from "@mui/material";

import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";

import { useTranslation } from "react-i18next";

const emptyEmployee = {
  noEmploye: null,
  firstName: "",
  lastName: "",
  username: "",
  email: "",
  langue: "FR",
  nodooliz: "",
  departmentId: "",
  department: "",
  estOperateur: false,
  isInactive: false,
};

const EditEmployeeModal = ({
  open,
  employee,
  departments = [],
  onClose,
  onSave,
}) => {
  const { t, i18n } = useTranslation("UserManagement");

  const [form, setForm] = useState(emptyEmployee);

  useEffect(() => {
    if (employee) {
      setForm({
        noEmploye: employee.noEmploye,
        firstName: employee.firstName || "",
        lastName: employee.lastName || "",
        username: employee.username || "",
        email: employee.email || "",
        langue: employee.langue || "FR",
        nodooliz: employee.nodooliz || "",
        departmentId: employee.departmentId || "",
        department: employee.department || "",
        estOperateur: employee.estOperateur || false,
        isInactive: employee.isInactive || false,
      });
    } else {
      setForm({
        ...emptyEmployee,
      });
    }
  }, [employee, open]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    const data = {
      ...form,

      // sécurité ADD
      estOperateur: employee ? form.estOperateur : false,

      isInactive: employee ? form.isInactive : false,
    };

    onSave(data);
  };
  const isEditMode = !!employee;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
        },
      }}
    >
      {/* HEADER */}

      <DialogTitle
        sx={{
          background: "linear-gradient(135deg,#1976d2,#42a5f5)",

          color: "white",
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          {employee ? <EditRoundedIcon /> : <PersonAddRoundedIcon />}

          <Box>
            <Typography variant="h6" fontWeight={700}>
              {employee ? t("EditEmployee") : t("AddEmployee")}
            </Typography>

            <Typography variant="body2">{t("EmployeeInformation")}</Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      {/* ================= CONTENT ================= */}

      <DialogContent sx={{ background: "#fafafa", p: 3 }}>
        {/* ================= PERSONAL ================= */}

        <Box
          sx={{
            background: "white",

            borderRadius: 3,

            p: 3,

            mb: 3,
          }}
        >
          <Box display="flex" gap={1} alignItems="center" mb={3}>
            <PersonRoundedIcon color="primary" />

            <Typography fontWeight={700}>{t("PersonalInformation")}</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("FirstName")}
                value={form.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("LastName")}
                value={form.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("Username")}
                disabled={!!employee}
                value={form.username}
                onChange={(e) => handleChange("username", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("Email")}
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label={t("Language")}
                value={form.langue}
                onChange={(e) => handleChange("langue", e.target.value)}
              >
                <MenuItem value="FR">🇫🇷 {t("French")}</MenuItem>

                <MenuItem value="EN">🇬🇧 {t("English")}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("NoDooliz")}
                value={form.nodooliz}
                onChange={(e) => handleChange("nodooliz", e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* ================= COMPANY ================= */}

        <Box
          sx={{
            background: "white",

            borderRadius: 3,

            p: 3,

            mb: 3,
          }}
        >
          <Box display="flex" gap={1} alignItems="center" mb={3}>
            <ApartmentRoundedIcon color="primary" />

            <Typography fontWeight={700}>{t("CompanyInformation")}</Typography>
          </Box>

          <TextField
            select
            fullWidth
            label={t("Department")}
            value={form.departmentId}
            disabled={isEditMode && form.estOperateur}
            onChange={(e) => handleChange("departmentId", e.target.value)}
          >
            <MenuItem value="">{t("Select")}</MenuItem>

            {departments
              .filter((d) => {
                // Si l'employé n'est pas opérateur
                // on cache Franchisee
                if (
                  !form.estOperateur &&
                  (d.textEn === "Franchisee" || d.textFr === "Franchisé")
                ) {
                  return false;
                }

                return true;
              })
              .map((d) => (
                <MenuItem
                  key={d.id}
                  value={d.id}
                  disabled={
                    isEditMode &&
                    form.estOperateur &&
                    (d.textEn === "Franchisee" || d.textFr === "Franchisé")
                  }
                >
                  {i18n.language === "FR" ? d.textFr : d.textEn}
                </MenuItem>
              ))}
          </TextField>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* ================= OPTIONS ================= */}

        <Box
          sx={{
            background: "white",

            borderRadius: 3,

            p: 3,
          }}
        >
          <Box display="flex" gap={1} alignItems="center" mb={2}>
            <AdminPanelSettingsRoundedIcon color="primary" />

            <Typography fontWeight={700}>{t("Options")}</Typography>
          </Box>

          <FormControlLabel
            control={<Checkbox checked={form.estOperateur} disabled />}
            label={t("Operator")}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={!form.isInactive}
                onChange={(e) => handleChange("isInactive", !e.target.checked)}
              />
            }
            label={t("Active")}
          />
        </Box>
      </DialogContent>

      <Divider />

      {/* FOOTER */}

      <DialogActions sx={{ p: 2 }}>
        <Button startIcon={<CloseRoundedIcon />} onClick={onClose}>
          {t("Cancel")}
        </Button>

        <Button
          variant="contained"
          startIcon={<SaveRoundedIcon />}
          onClick={handleSubmit}
        >
          {t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEmployeeModal;
